import { translatedClientError } from "../../i18n/client-error.js";
import { apiErrorText } from "../../api/api-error.js";
import { portalText, usePortalTranslation } from "../../i18n/portal.js";
/**
 * `data-screen-label="New campaign"` — plan unit P14-02, the form that starts a campaign.
 *
 * AMENDED 2026-10-09 (design sync, the Claude Design export of 2026-10-09). The screen is the new
 * export's: one form column (560 px) beside a sticky ESTIMATE panel that holds Start campaign — on a
 * window under 1000 px the panel becomes a sticky one-line bar above the form — and, in order: the
 * agent (only when opened from a contact list), the campaign name with its rule and a live check,
 * who to call with "Need a different audience? Create a contact list", the questions as a question-set
 * card, people already surveyed (Ask them again / Skip them), the days as chips with a sentence, the
 * calling hours "in each person's local time" with how that is worked out, calls at the same time,
 * attempts, the wait before trying again as four cards (only with a retry), and "When it runs" (starts,
 * first call, ends). Cancel is gone from the design; Back stays.
 *
 * WHAT IS KEPT FROM BEFORE, BECAUSE THE PLATFORM NEEDS IT:
 *
 *   · **Calls from** (plan-008 ST-UA): a campaign rings through the account's own SIP trunk, and the API
 *     refuses a draft with no caller-ID number (`422 caller_id_not_bound`, printed at the field). The
 *     design does not draw it; it sits under "Who to call".
 *   · **The hours are the person's, not the workspace's.** They travel as bare `HH:MM`; the dialler reads
 *     each recipient's own clock and skips anyone whose zone it cannot resolve.
 *   · **The estimate is a band.** "Talk-time cost" prints the API's low–high band with its assumption,
 *     never one figure, and people the estimate leaves out for want of a time zone are named (CL #118).
 *   · **The ceiling is the organization's concurrency entitlement**; the slider cannot pass it. AMENDED 2026-10-28
 *     (campaign fix-up, ADR 0045): **and the agent's own line limit** (its Capacity, else its live version's), so the
 *     slider stops at the lower of the two, opens at min(10, that), and — when the agent's limit is the lower — says so
 *     and where to raise it. Above it every extra dial would be refused and the campaign would only slow down.
 *   · **What Start still needs is printed in words** under the button (2026-10-04).
 *
 * WHAT THE DESIGN DRAWS THAT IS NOT BUILT, AND WHY:
 *
 *   · **The "Questions to ask" picker, other question sets and "Upload a new question set…".** A survey agent asks
 *     the questionnaire its live version was built with (design §16.7.2 AS AMENDED 2026-10-04; owner decision
 *     2026-10-09, answer 40: "On a campaign we set an agent; we do not change the questions — the campaign uses
 *     what the agent supports."), so the card shows that set read-only and there is nothing to pick or upload. To
 *     change the questions, change the agent (a new version). An agent with no questionnaire keeps the typed editor.
 *   · **"about 2 min a call" and the set's one-paragraph story.** Nothing records either for a
 *     questionnaire, so the card prints the facts it can count.
 *
 * AMENDED 2026-10-09 (plan-015 NAM, G18, design §28.5.10, owner answer 7): the name is checked as it is typed with
 * the API's own rule (`campaignNameProblem`, `nameKeyOf` from `@wetalk/domain`), so the field says "Looks good." only
 * to a name the API accepts. A name is unique among the WORKSPACE's campaigns, so the check reads the campaigns of
 * every agent that can run one, not only the chosen agent's; a name the console could not see (another member's
 * campaign created a moment ago) comes back as `409 name_taken` and is printed at the name, in the design's words.
 *
 * AMENDED 2026-10-09 (plan-015 CMP, G16, G17, owner answers 5 and 6): the start and the end are each person's own.
 * "Starts" and "First call" travel as `start_local_date` / `start_local_time` and **"Ends" is enabled** — an
 * optional date (empty = no end) sent as `end_local_date` — read on every contact's own clock; the start note is
 * the design's ("Starts <day> at <time>, in each person's local time. Editable until then.") and the end note is
 * the prototype's (until the last retry; everyone done by; retries after it skipped; first calls late). **Edit
 * before start:** on REG's `/campaigns/$campaignId/edit` (the id read with `useParams({ strict: false })`) the same
 * form opens on the stored campaign, "Save changes" sends `updateCampaign` (`PATCH`), and a campaign that has
 * started — or a gateway that cannot save — says so instead of offering the form. What a campaign keeps from its
 * creation (its agent, the number it calls from, its questions, people already surveyed) is shown, not changed.
 */
import { keepPreviousData, useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useLocation, useNavigate, useParams } from "@tanstack/react-router";
import { ApiError } from "@wetalk/api-client";
import { CAMPAIGN_NAME_MAXIMUM, CAMPAIGN_NAME_TAKEN_MESSAGE, campaignNameProblem, isNameTaken } from "@wetalk/domain";
import { Button, Field, Icon, Input, Segmented, Select, classes } from "@wetalk/ui";
import { useId, useRef, useState, useSyncExternalStore, type ReactNode } from "react";

import { VoiceAgentQuestionList } from "./voice-agent-question-list.js";
import { CampaignQuestionEditor } from "./campaign-question-editor.js";
import {
  VOICE_AGENT_QUESTION_NOTE,
  ATTEMPT_OPTION,
  CAMPAIGN_FINISHED_TEXT,
  DAY_OPTION,
  VOICEMAIL_SENTENCE,
  retryRuleSentenceOf,
  callerIdChoicesOf,
  callerIdGapOf,
  campaignLineLimitHint,
  campaignLineBoundOf,
  campaignLineBoundText,
  campaignRefusalText,
  countText,
  missingOf,
  outboundRateTextOf,
  questionDraftsOf,
  type CampaignLineBound,
  type DraftQuestionSource,
  type QuestionEntry,
} from "./campaign-copy.js";
import {
  CAMPAIGN_NAME_TIP,
  REPEAT_OPTION,
  WAIT_CHOICE,
  WAIT_TIP,
  campaignPlanOf,
  dayTextOf,
  dmyOf,
  dmyOfIsoDate,
  endDateProblem,
  endNoteOf,
  isoDateOfDmy,
  lastDialDayOf,
  questionMixOf,
  repeatNoteOf,
  startMomentOf,
  startNoteOf,
  startsAtOfMoment,
  type CampaignPlan,
} from "./new-campaign-plan.js";
import { campaignDraftOf, withCampaignReturn, type CampaignFormDraft } from "./campaign-return.js";
import {
  DEFAULT_CALLING_HOURS,
  callingDaysOf,
  callingHoursDraftOf,
  callingHoursProblemText,
  callingRangesOf,
  type CallingHoursDraft,
} from "./calling-hours.js";
import { CallingHoursEditor } from "./calling-hours-editor.js";
import { CAMPAIGN_GLYPH } from "./campaign-glyph.js";
import { InfoTip } from "./info-tip.js";
import { GLYPH } from "../../shell/glyph.js";
import { useGateway } from "../../api/provider.js";
import { useHelpFillable } from "../../help/fill/use-help-fillable.js";
import { sipTrunkListQuery } from "../numbers/sip-trunk-query.js";
import {
  QUERY_KEY,
  campaignEstimateQuery,
  campaignQuery,
  campaignListQuery,
  campaignSuggestionQuery,
  concurrencyQuery,
  organizationConcurrencyQuery,
  contactListQuery,
  voiceAgentListQuery,
  voiceAgentQuery,
  voiceAgentQuestionnaireQuery,
} from "../../api/query.js";
import { formatMoney } from "../../money.js";
import type { CampaignDraftView, CampaignEstimateView, ReturningPersonTreatmentView } from "../../api/gateway.js";
import {
  CampaignWorkspaceLimitError,
  type CampaignCreateDraftView,
  type CampaignDetailView,
  type CampaignEditView,
} from "../../api/slice/campaign.js";

import "./campaign.css";

export interface NewCampaignScreenProps {
  /** Which agent runs the campaign. Empty when the screen was opened from a list. */
  readonly voiceAgentId: string;
  /** Pre-chosen by "Use in campaign", or the list just made on Contact lists. Empty when none is. */
  readonly contactListId: string;
}

/** plan-015 CMP: what a campaign keeps from its creation, said where an edit shows it instead of changing it. */
const FIXED_AT_CREATION = () => (portalText("operations", "set_when_the_campaign_was_created_it_stays_as_it_is_bd5d1fa5"));

/** 2026-10-30: what a started campaign keeps — its contact list and its start — said where the edit shows them. */
const FIXED_ONCE_STARTED = () => (portalText("operations", "fixed_once_the_campaign_has_started_e611b2a3"));

/** The design's default: ten lines, unless the organization or the agent allows fewer (campaign fix-up 2026-10-28). */
const DEFAULT_SIMULTANEOUS_CALL = 10;

/** The prototype's breakpoint for the side estimate panel (`s.vw >= 1000`): 62.5em of 16 px. */
const WIDE_SCREEN = "(min-width: 62.5em)";

function subscribeWide(change: () => void): () => void {
  const query = globalThis.matchMedia?.(WIDE_SCREEN);
  query?.addEventListener("change", change);
  return () => {
    query?.removeEventListener("change", change);
  };
}

/** Whether the window is wide enough for the side panel; wide where it cannot be told. */
function useWideScreen(): boolean {
  return useSyncExternalStore(
    subscribeWide,
    () => globalThis.matchMedia?.(WIDE_SCREEN).matches !== false,
    () => true,
  );
}

export function NewCampaignScreen(props: NewCampaignScreenProps): ReactNode {
  usePortalTranslation("operations");
  // plan-015 CMP: REG's `/campaigns/$campaignId/edit` renders this screen; only that route has the id.
  const params = useParams({ strict: false }) as { readonly campaignId?: string };
  if (params.campaignId !== undefined && params.campaignId !== "") return <EditCampaign campaignId={params.campaignId} />;
  return <CreateCampaign {...props} />;
}

function CreateCampaign(props: NewCampaignScreenProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const location = useLocation();
  const voiceAgent = useQuery({
    ...voiceAgentQuery(gateway, props.voiceAgentId),
    enabled: props.voiceAgentId !== "",
  });

  if (props.voiceAgentId !== "" && voiceAgent.isError) {
    return (
      <section className="c-page camp-page" data-screen-label="New campaign">
        <ReturnLink voiceAgentId="" contactListId={props.contactListId} />
        <h1 className="c-camp-title">{portalText("operations", "new_campaign_aaf9a8a4")}</h1>
        <p className="c-note c-note-critical">
          {portalText("operations", "we_could_not_load_the_agent_this_campaign_is_for_so_the_fo_c719564a")}{" "}
          {campaignRefusalText(voiceAgent.error)}
        </p>
      </section>
    );
  }
  if (props.voiceAgentId !== "" && voiceAgent.data === undefined) {
    return (
      <section className="c-page camp-page" data-screen-label="New campaign">
        <p className="c-note">{portalText("operations", "opening_the_agent_82f35488")}</p>
      </section>
    );
  }
  return (
    <NewCampaignForm
      fixedVoiceAgentId={props.voiceAgentId}
      voiceAgentName={voiceAgent.data === undefined ? null : voiceAgent.data.displayName}
      initialContactListId={props.contactListId}
      restored={campaignDraftOf(location.state)}
      editing={null}
    />
  );
}

/**
 * plan-015 CMP (G17): the stored campaign, opened as an edit. AMENDED 2026-10-30 (owner): until it finishes — a started
 * one keeps its contact list and its start (`lockedField`). A finished or ended campaign says so — with the way back —
 * instead of offering a form that cannot act.
 */
function EditCampaign({ campaignId }: { readonly campaignId: string }): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const campaign = useQuery(campaignQuery(gateway, campaignId));
  const voiceAgentId = campaign.data?.voiceAgentId ?? "";
  const voiceAgent = useQuery({ ...voiceAgentQuery(gateway, voiceAgentId), enabled: voiceAgentId !== "" });
  const back = (
    <Link to="/campaigns/$campaignId" params={{ campaignId }} className="c-backlink camp-back">
      <Icon glyph={GLYPH.arrowLeft} size="sm" />
      {portalText("operations", "back_to_the_campaign_367b1404")}</Link>
  );
  if (campaign.isError) {
    return (
      <section className="c-page camp-page" data-screen-label="New campaign">
        {back}
        <h1 className="c-camp-title">{portalText("operations", "edit_campaign_e73cfe91")}</h1>
        <p className="c-note c-note-critical">
          {portalText("operations", "we_could_not_load_this_campaign_so_the_form_is_not_shown_1ee5670e")}{campaignRefusalText(campaign.error)}
        </p>
      </section>
    );
  }
  if (campaign.data === undefined) {
    return (
      <section className="c-page camp-page" data-screen-label="New campaign">
        <p className="c-note">{portalText("operations", "opening_the_campaign_6a784727")}</p>
      </section>
    );
  }
  if (!campaign.data.editable) {
    return (
      <section className="c-page camp-page" data-screen-label="New campaign">
        {back}
        <h1 className="c-camp-title">{portalText("operations", "edit_campaign_e73cfe91")}</h1>
        <p className="c-note" role="status">
          {CAMPAIGN_FINISHED_TEXT()}
        </p>
      </section>
    );
  }
  return (
    <NewCampaignForm
      fixedVoiceAgentId={campaign.data.voiceAgentId}
      voiceAgentName={voiceAgent.data === undefined ? null : voiceAgent.data.displayName}
      initialContactListId={campaign.data.contactListId ?? ""}
      restored={null}
      editing={campaign.data}
    />
  );
}

/** The attempts as the form's choice, when they are one of its three; otherwise the stored number is kept. */
function attemptChoiceOf(attempts: number | undefined): "1" | "2" | "3" {
  return attempts === 1 ? "1" : attempts === 3 ? "3" : "2";
}

/**
 * Where "Back" goes: the agent's Outbound tab when the screen was opened from an agent, Contact lists
 * when it was opened from "Use in campaign", the agent list otherwise.
 */
function ReturnLink({ voiceAgentId, contactListId }: { readonly voiceAgentId: string; readonly contactListId: string }): ReactNode {
  usePortalTranslation("operations");
  const arrow = <Icon glyph={GLYPH.arrowLeft} size="sm" />;
  if (voiceAgentId !== "") {
    return (
      <Link to="/agents/$voiceAgentId" params={{ voiceAgentId }} search={{ tab: "outbound" }} className="c-backlink camp-back">
        {arrow}
        {portalText("operations", "back_to_campaigns_ed9aa177")}</Link>
    );
  }
  if (contactListId !== "") {
    return (
      <Link to="/contact-lists" className="c-backlink camp-back">
        {arrow}
        {portalText("operations", "back_to_contact_lists_42a52ae1")}</Link>
    );
  }
  return (
    <Link to="/agents" className="c-backlink camp-back">
      {arrow}
      {portalText("operations", "back_to_agents_c3c9c2a6")}</Link>
  );
}

// -------------------------------------------------------------------------------------------
// The form
// -------------------------------------------------------------------------------------------

interface NewCampaignFormProps {
  /** From the URL; empty when the member must choose the agent here. */
  readonly fixedVoiceAgentId: string;
  readonly voiceAgentName: string | null;
  readonly initialContactListId: string;
  /** The form as it was left for Contact lists, when it comes back from there. */
  readonly restored: CampaignFormDraft | null;
  /** plan-015 CMP: the stored campaign this form edits, or `null` for a new one. */
  readonly editing: CampaignDetailView | null;
}

function NewCampaignForm({ fixedVoiceAgentId, voiceAgentName, initialContactListId, restored, editing }: NewCampaignFormProps): ReactNode {
  usePortalTranslation("operations");
  const gateway = useGateway();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const wide = useWideScreen();

  const missingId = useId();
  const nameHelpId = useId();
  const waitLabelId = useId();
  const keyCounter = useRef(0);
  const nextKey = (): string => {
    keyCounter.current += 1;
    return `entry-${String(keyCounter.current)}`;
  };

  const [chosenVoiceAgentId, setChosenVoiceAgentId] = useState(restored?.chosenVoiceAgentId ?? "");
  const [name, setName] = useState(editing?.name ?? restored?.name ?? "");
  const [contactListId, setContactListId] = useState(initialContactListId);
  const [question, setQuestion] = useState<readonly QuestionEntry[]>(
    () => restored?.question ?? [{ key: "entry-primary-1", text: "", kind: "scale" }],
  );
  const [returningPerson, setReturningPerson] = useState<ReturningPersonTreatmentView>(
    (editing?.returningPerson ?? restored?.returningPerson) === "skip_them" ? "skip_them" : "same_questions",
  );
  const [attempt, setAttempt] = useState<"1" | "2" | "3">(
    editing === null ? (restored?.attempt ?? "2") : attemptChoiceOf(editing.attemptsPerPerson),
  );
  // plan-015 CMP: an edit keeps a stored number of attempts the three choices cannot show, until one is picked.
  const [keptAttempts, setKeptAttempts] = useState<number | null>(
    editing?.attemptsPerPerson !== undefined && ![1, 2, 3].includes(editing.attemptsPerPerson) ? editing.attemptsPerPerson : null,
  );
  const [waitMinute, setWaitMinute] = useState(editing?.waitBetweenAttemptMinute ?? restored?.waitMinute ?? 1_440);
  // 2026-10-30: the calling ranges, the same every day or different per day (./calling-hours.ts).
  const [hoursDraft, setHoursDraft] = useState<CallingHoursDraft>(() =>
    editing !== null
      ? callingHoursDraftOf(editing.schedule.callingHours)
      : restored !== null
        ? callingHoursDraftOf(restored.callingHours)
        : DEFAULT_CALLING_HOURS,
  );
  const callingHours = callingRangesOf(hoursDraft);
  const callingDayCount = callingDaysOf(callingHours).length;
  const [simultaneousCall, setSimultaneousCall] = useState(
    editing?.schedule.simultaneousCall ?? restored?.simultaneousCall ?? DEFAULT_SIMULTANEOUS_CALL,
  );
  const [startDateText, setStartDateText] = useState(() =>
    editing?.startLocalDate !== undefined && editing.startLocalDate !== null
      ? dmyOfIsoDate(editing.startLocalDate)
      : (restored?.startDateText ?? dmyOf(new Date())),
  );
  const [startTime, setStartTime] = useState(editing?.startLocalTime ?? restored?.startTime ?? "10:00");
  // plan-015 CMP (G16): the end date, optional — empty is no end.
  const [endDateText, setEndDateText] = useState(() =>
    editing?.endLocalDate !== undefined && editing.endLocalDate !== null
      ? dmyOfIsoDate(editing.endLocalDate)
      : (restored?.endDateText ?? ""),
  );
  const [chosenCallerId, setChosenCallerId] = useState(restored?.callerId ?? "");

  const voiceAgentList = useQuery({ ...voiceAgentListQuery(gateway), enabled: fixedVoiceAgentId === "" });
  // CL #134: only an agent built for outbound campaigns, with a published version, can run one.
  const campaignVoiceAgent = voiceAgentList.data?.filter(runsCampaigns) ?? [];
  const otherVoiceAgent = voiceAgentList.data?.filter((one) => !runsCampaigns(one)) ?? [];
  // With exactly one agent that can run it, the design shows that agent chosen.
  const onlyVoiceAgentId = campaignVoiceAgent.length === 1 ? (campaignVoiceAgent[0]?.voiceAgentId ?? "") : "";
  const pickedVoiceAgentId = chosenVoiceAgentId !== "" ? chosenVoiceAgentId : onlyVoiceAgentId;
  const voiceAgentId = fixedVoiceAgentId !== "" ? fixedVoiceAgentId : pickedVoiceAgentId;

  // 2026-10-04: what the chosen agent asks — its own questionnaire, or (with none) a typed list.
  const questionnaire = useQuery({ ...voiceAgentQuestionnaireQuery(gateway, voiceAgentId), enabled: voiceAgentId !== "" });
  const questionSource: DraftQuestionSource =
    questionnaire.data === undefined ? "unknown" : questionnaire.data.asksQuestionnaire ? "questionnaire" : "typed";

  const contactList = useQuery(contactListQuery(gateway));
  // plan-015 NAM: names are unique in the workspace, so the live check reads every campaign-running agent's campaigns.
  const workspaceVoiceAgent = useQuery(voiceAgentListQuery(gateway));
  const campaignOwnerId = [
    ...new Set([voiceAgentId, ...(workspaceVoiceAgent.data?.filter(runsCampaigns).map((one) => one.voiceAgentId) ?? [])]),
  ].filter((one) => one !== "");
  const existingCampaign = useQueries({
    queries: campaignOwnerId.map((one) => campaignListQuery(gateway, one)),
    // An edit's own name is not "another campaign's" (plan-015 CMP).
    combine: (found) =>
      found.flatMap((one) => one.data?.filter((row) => row.campaignId !== editing?.campaignId).map((row) => row.name) ?? []),
  });

  // Opened from an agent with no list chosen, the form takes the gateway's suggestion once: the
  // sandbox's worked example opens on the design's list, the API proposes nothing.
  const suggestion = useQuery({
    ...campaignSuggestionQuery(gateway, fixedVoiceAgentId),
    enabled: fixedVoiceAgentId !== "" && initialContactListId === "",
  });
  const [suggestionTaken, setSuggestionTaken] = useState(false);
  if (!suggestionTaken && suggestion.data !== undefined) {
    setSuggestionTaken(true);
    if (suggestion.data !== null && contactListId === "") setContactListId(suggestion.data.contactListId);
  }
  // INT 2026-10-07: the organization's concurrency entitlement (the usage summary's), which the API's campaign ceiling is.
  const concurrency = useQuery(organizationConcurrencyQuery(gateway));
  // Campaign fix-up 2026-10-28 (ADR 0045): the chosen agent's own line limit bounds the campaign as well.
  const voiceAgentConcurrency = useQuery({ ...concurrencyQuery(gateway, voiceAgentId), enabled: voiceAgentId !== "" });
  // plan-008: the numbers this agent may call from — caller-ID numbers bound to it on a verified trunk.
  const trunk = useQuery(sipTrunkListQuery(gateway));
  const callerIdChoice = trunk.data === undefined || voiceAgentId === "" ? [] : callerIdChoicesOf(trunk.data, voiceAgentId);
  const callerIdGap = trunk.data === undefined || voiceAgentId === "" ? null : callerIdGapOf(trunk.data, voiceAgentId);
  // A single choice is chosen for the member; a choice that stopped being one (another agent) is dropped.
  const callerId =
    callerIdChoice.find((one) => one.phoneNumberId === chosenCallerId)?.phoneNumberId ??
    (callerIdChoice.length === 1 ? (callerIdChoice[0]?.phoneNumberId ?? "") : "");
  // plan-015 CMP: an edit keeps the number the campaign calls from.
  const keptCallerIdChosen = editing !== null && editing.callerIdPhoneNumberId !== null;
  // 2026-10-30: an edit after the start keeps the list and the start, and ends today at the earliest.
  const started = editing !== null && editing.lockedField.length > 0;

  const windowProblem = callingHoursProblemText(callingHours);
  const nameCheck = campaignNameCheckOf(name, existingCampaign);
  const now = new Date();
  const startMoment = startMomentOf(startDateText, startTime);
  const startNote = startNoteOf({ dateText: startDateText, time: startTime, callingHours, now });
  const startPassed = startMoment !== null && startMoment.getTime() < new Date(now).setHours(0, 0, 0, 0);
  // A started campaign's end is checked against today only once it is changed: the API checks it on the workspace's
  // clock, and a stored end that is today somewhere else must not block a rename.
  const endLocalDate = endDateText.trim() === "" ? null : isoDateOfDmy(endDateText);
  const endChanged = editing === null || endLocalDate !== editing.endLocalDate;
  const startedBy = started && endChanged ? now : null;
  const endProblem = endDateProblem(endDateText, startDateText, startedBy);
  const startLocalDate = isoDateOfDmy(startDateText);

  // The slider's ceiling is the lower of the organization's concurrency entitlement and the agent's own line limit
  // (campaign fix-up 2026-10-28). The estimate states both authoritatively once it has answered for THIS agent; until
  // then the usage summary's figure and the agent's concurrency summary bound the first ask, so it is never above the
  // agent's limit (which the API refuses). An agent whose summary cannot be read leaves its rung to the API.
  const [estimateBound, setEstimateBound] = useState<{ readonly voiceAgentId: string; readonly bound: CampaignLineBound } | null>(null);
  const voiceAgentLimitRead = voiceAgentId !== "" && !voiceAgentConcurrency.isPending;
  const firstBound =
    concurrency.data === undefined || !voiceAgentLimitRead
      ? null
      : campaignLineBoundOf(concurrency.data, voiceAgentConcurrency.data?.channelUnitCeiling ?? null, null);
  const bound = estimateBound !== null && estimateBound.voiceAgentId === voiceAgentId ? estimateBound.bound : firstBound;
  const ceiling = bound === null ? null : bound.ceiling;
  const lines = ceiling === null ? simultaneousCall : Math.min(simultaneousCall, ceiling);

  const draftOf = (atOnce: number): CampaignDraftView => ({
    voiceAgentId,
    contactListId,
    name: name.trim(),
    // A questionnaire agent takes no list (the API refuses one, `campaign_question_not_accepted`).
    question: questionSource === "typed" ? questionDraftsOf(question, "primary") : [],
    returningPerson,
    returningQuestion: null,
    attemptsPerPerson: Number(attempt),
    waitBetweenAttemptMinute: waitMinute,
    callingHours,
    simultaneousCall: atOnce,
    startsAt: startsAtOfMoment(startMoment, now),
    // plan-017 (Q5, OQ-1): the number it dials from picks the route, and so the outbound rate the estimate is at.
    ...(callerId === "" ? {} : { callerIdPhoneNumberId: callerId }),
  });

  // An estimate needs the agent (the API refuses one without it — review 2026-09-25, CL #134).
  const estimateReady =
    voiceAgentId !== "" && contactListId !== "" && callingDayCount > 0 && windowProblem === null && voiceAgentLimitRead;
  const estimate = useQuery({
    ...campaignEstimateQuery(gateway, draftOf(lines)),
    enabled: estimateReady,
    // A new slider position must not blank the band (or the ceiling) while it is re-asked.
    placeholderData: keepPreviousData,
  });
  // plan-031 VMD-WAPP (design §42.11): the voicemail detection switch, from the stored campaign or the estimate; `null`
  // until either has answered, so the voicemail and retry sentences claim nothing in the meantime.
  const voicemailDetection =
    editing !== null ? editing.voicemailDetection : estimate.data === undefined ? null : estimate.data.voicemailDetection;
  const retryRule = retryRuleSentenceOf(voicemailDetection);

  // Campaign fix-up 2026-10-28: the workspace's own line limit is read only from the estimate — its figures, or the
  // refusal that states it — so a first ask above it stops the slider there and asks again at that figure.
  if (estimate.error instanceof CampaignWorkspaceLimitError && firstBound !== null) {
    const refused = campaignLineBoundOf(firstBound.entitlement, firstBound.voiceAgentLimit, estimate.error.workspaceChannelUnitLimit);
    if (estimateBound === null || estimateBound.voiceAgentId !== voiceAgentId || estimateBound.bound.ceiling !== refused.ceiling) {
      setEstimateBound({ voiceAgentId, bound: refused });
    }
  }

  if (estimate.data !== undefined) {
    const answered = campaignLineBoundOf(
      estimate.data.entitlementCeiling,
      estimate.data.voiceAgentChannelUnitLimit,
      estimate.data.workspaceChannelUnitLimit,
    );
    if (
      estimateBound === null ||
      estimateBound.voiceAgentId !== voiceAgentId ||
      estimateBound.bound.ceiling !== answered.ceiling ||
      estimateBound.bound.entitlement !== answered.entitlement ||
      estimateBound.bound.voiceAgentLimit !== answered.voiceAgentLimit ||
      estimateBound.bound.workspaceLimit !== answered.workspaceLimit
    ) {
      setEstimateBound({ voiceAgentId, bound: answered });
    }
  }

  const create = useMutation<CampaignDetailView, Error, CampaignCreateDraftView>({
    mutationFn: (draft) => gateway.createCampaign(draft),
    onSuccess: async (made) => {
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.campaign });
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.contactList });
      await navigate({ to: "/campaigns/$campaignId", params: { campaignId: made.campaignId } });
    },
  });
  // plan-015 CMP (G17): Save changes, before the campaign starts (`409 campaign_started` after).
  const update = useMutation<CampaignDetailView, Error, { readonly campaignId: string; readonly edit: CampaignEditView }>({
    mutationFn: ({ campaignId, edit }) => gateway.updateCampaign(campaignId, edit),
    onSuccess: async (saved) => {
      queryClient.setQueryData(campaignQuery(gateway, saved.campaignId).queryKey, saved);
      await queryClient.invalidateQueries({ queryKey: QUERY_KEY.campaign });
      await navigate({ to: "/campaigns/$campaignId", params: { campaignId: saved.campaignId } });
    },
  });
  const save = editing === null ? create : update;

  const missing = missingOf({
    voiceAgentChosen: voiceAgentId !== "",
    // An edit keeps the campaign's questions, so nothing typed is asked of it here.
    questionSource: editing === null ? questionSource : "questionnaire",
    callerIdChosen: editing === null ? callerId !== "" : keptCallerIdChosen,
    name,
    contactListId,
    question,
    returningPerson,
    returningQuestion: [],
    callingHours,
    start: "as_soon_as_ready",
    startsAtLocal: "",
  });
  if (nameCheck.invalid) missing.push(portalText("operations", "fix_the_campaign_name_value_f55c6740", { v0: String(nameCheck.message) }));
  if (!started && (startMoment === null || startPassed)) missing.push(portalText("operations", "fix_when_it_starts_value_2e490afa", { v0: String(startNote.text) }));
  if (endProblem !== null) missing.push(portalText("operations", "fix_when_it_ends_value_f77e76c4", { v0: String(endProblem) }));

  // Opened from a contact list, the agent is chosen here, so the kicker names it once chosen.
  const chosenVoiceAgent = voiceAgentList.data?.find((one) => one.voiceAgentId === pickedVoiceAgentId);
  const kickerName = voiceAgentName !== null ? voiceAgentName : chosenVoiceAgent === undefined ? null : chosenVoiceAgent.displayName;

  // A refusal that names the caller-ID number or the name is printed at its field; every other one under Start.
  const callerIdRefusal =
    save.error instanceof ApiError && save.error.field === "caller_id_phone_number_id" ? campaignRefusalText(save.error) : null;
  const nameRefusal = save.error instanceof ApiError && save.error.field === "name" ? apiErrorText(save.error, "") : null;
  const nameHelp = nameRefusal !== null ? { message: nameRefusal, invalid: true } : nameCheck;

  const changeName = (next: string): void => {
    setName(next);
    if (nameRefusal !== null) save.reset();
  };
  const chooseAttempt = (next: "1" | "2" | "3"): void => {
    setAttempt(next);
    setKeptAttempts(null);
  };
  // plan-019 WAPP-FORM: the calling window the help assistant may propose, through the same setters as typing (research
  // form-fill §5b). The wait is bound only while it is on screen (more than one attempt). Never the agent, the contact
  // list, the caller id, the questions, the returning-person choice or the simultaneous-conversation slider.
  const simpleHours = hoursDraft.mode === "same" && hoursDraft.ranges.length === 1;
  const firstRange = hoursDraft.ranges[0] ?? { from: "", to: "" };
  const helpFill = useHelpFillable(editing === null ? "campaign_calling_window" : "campaign_edit_calling_window", {
    name: { value: name, set: changeName },
    // 2026-10-30: the assistant's window fills the simple form only — the same hours every day, one range.
    day_of_week: simpleHours
      ? {
          value: hoursDraft.days,
          set: (days) => {
            setHoursDraft((held) => ({ ...held, days }));
          },
          accepts: (next) => next.every((day) => DAY_OPTION.some((one) => one.day === day)),
        }
      : undefined,
    from_local_time: simpleHours
      ? {
          value: firstRange.from,
          set: (from) => {
            setHoursDraft((held) => ({ ...held, ranges: held.ranges.map((one, at) => (at === 0 ? { ...one, from } : one)) }));
          },
        }
      : undefined,
    to_local_time: simpleHours
      ? {
          value: firstRange.to,
          set: (to) => {
            setHoursDraft((held) => ({ ...held, ranges: held.ranges.map((one, at) => (at === 0 ? { ...one, to } : one)) }));
          },
        }
      : undefined,
    attempt: {
      value: attempt,
      set: (next) => {
        const choice = ATTEMPT_OPTION.find((one) => one.value === next);
        if (choice !== undefined) chooseAttempt(choice.value);
      },
      accepts: (next) => ATTEMPT_OPTION.some((one) => one.value === next),
    },
    wait_minute:
      attempt === "1"
        ? undefined
        : {
            value: String(waitMinute),
            set: (next) => {
              setWaitMinute(Number(next));
            },
            accepts: (next) => WAIT_CHOICE.some((one) => String(one.minute) === next),
          },
    // A started campaign keeps its start, so the help assistant is offered neither field.
    start_date: started
      ? undefined
      : {
          value: isoDateOfDmy(startDateText),
          set: (next) => {
            setStartDateText(dmyOfIsoDate(next));
          },
        },
    start_time: started ? undefined : { value: startTime, set: setStartTime },
    end_date: {
      value: endDateText.trim() === "" ? null : isoDateOfDmy(endDateText),
      set: (next) => {
        setEndDateText(dmyOfIsoDate(next));
      },
      unset: () => {
        setEndDateText("");
      },
    },
  });

  const plan: CampaignPlan | null =
    estimate.data === undefined || startMoment === null
      ? null
      : campaignPlanOf({
          start: startMoment.getTime() > now.getTime() ? startMoment : now,
          callingHours,
          attemptsPerPerson: Number(attempt),
          waitMinute,
          firstAttemptDayCount: estimate.data.dayCount,
        });

  const endNote = endNoteOf({ endDateText, startDateText, plan, startedBy });

  /** "Need a different audience?": Contact lists, with the form kept for the way back. */
  const makeList = (): void => {
    const draft: CampaignFormDraft = {
      chosenVoiceAgentId,
      name,
      returningPerson,
      attempt,
      waitMinute,
      callingHours,
      simultaneousCall,
      startDateText,
      startTime,
      endDateText,
      callerId: chosenCallerId,
      question,
    };
    void navigate({
      to: "/contact-lists",
      state: (previous) => withCampaignReturn(previous, { voiceAgentId: fixedVoiceAgentId, contactListId, draft }),
    });
  };

  const start = (
    <Button
      variant="primary"
      disabled={missing.length > 0 || save.isPending || startLocalDate === null}
      aria-describedby={missing.length === 0 ? undefined : missingId}
      onClick={() => {
        if (startLocalDate === null) return;
        const localStart = { startLocalDate, startLocalTime: startTime, endLocalDate };
        if (editing === null) {
          create.mutate({ ...draftOf(lines), callerIdPhoneNumberId: callerId, ...localStart });
          return;
        }
        update.mutate({
          campaignId: editing.campaignId,
          edit: {
            name: name.trim(),
            contactListId,
            callingHours,
            attemptsPerPerson: keptAttempts ?? Number(attempt),
            waitBetweenAttemptMinute: waitMinute,
            simultaneousCall: lines,
            ...localStart,
            // 2026-10-30: a started campaign keeps its list and its start; they are not sent.
            ...(started ? { keepsListAndStart: true as const } : {}),
          },
        });
      }}
    >
      {editing === null ? (create.isPending ? portalText("operations", "starting_e5f58095") : portalText("operations", "start_campaign_241d55c7")) : update.isPending ? portalText("operations", "saving_56a2285c") : portalText("operations", "save_changes_179359b3")}
    </Button>
  );
  const after = (
    <>
      {/* What is still needed, in words under Start and as its description (2026-10-04). */}
      {missing.length === 0 ? null : (
        <div className="c-camp-missing" id={missingId}>
          <span className="c-camp-missing-title">{portalText("operations", "before_it_can_start_a803568b")}</span>
          <ul className="c-camp-missing-list">
            {missing.map((one) => (
              <li key={one}>{one}</li>
            ))}
          </ul>
        </div>
      )}
      {save.isError && callerIdRefusal === null && nameRefusal === null ? (
        <p className="c-note c-note-critical" role="alert">
          {editing === null ? portalText("operations", "the_campaign_was_not_started_8f6a0cf5") : portalText("operations", "the_changes_were_not_saved_dfd52848")} {campaignRefusalText(save.error)}
        </p>
      ) : null}
    </>
  );

  const estimateState: EstimateState = {
    ready: estimateReady,
    voiceAgentChosen: voiceAgentId !== "",
    windowProblem,
    dayCount: callingDayCount,
    pending: estimate.isPending,
    refreshing: estimate.isPlaceholderData,
    failure: estimate.isError ? campaignRefusalText(estimate.error) : null,
    estimate: estimate.data,
    lines,
    attemptsPerPerson: keptAttempts ?? Number(attempt),
    plan,
    endDateText,
  };

  return (
    <section className="c-page camp-page" data-screen-label="New campaign">
      {editing === null ? (
        <ReturnLink voiceAgentId={fixedVoiceAgentId} contactListId={initialContactListId} />
      ) : (
        <Link to="/campaigns/$campaignId" params={{ campaignId: editing.campaignId }} className="c-backlink camp-back">
          <Icon glyph={GLYPH.arrowLeft} size="sm" />
          {portalText("operations", "back_to_the_campaign_367b1404")}</Link>
      )}
      <p className="c-camp-kicker">{kickerName === null ? portalText("operations", "outbound_2c7807c0") : portalText("operations", "value_outbound_1d7fd714", { v0: String(kickerName) })}</p>
      <h1 className="c-camp-title">{editing === null ? portalText("operations", "new_campaign_aaf9a8a4") : portalText("operations", "edit_campaign_e73cfe91")}</h1>
      <p className="c-camp-lede">
        {editing === null
          ? portalText("operations", "a_campaign_decides_which_questions_an_agent_should_ask_who_bec390cb")
          : started
            ? portalText("operations", "this_campaign_has_started_you_can_change_its_name_days_cal_cd714b4a")
            : portalText("operations", "you_can_change_this_campaign_until_it_starts_e31587d7")}
      </p>

      {fixedVoiceAgentId === "" ? (
        <div className="c-camp-agent">
          <Field label={portalText("operations", "agent_5ce2e6f4")}>
            {(control) =>
              voiceAgentList.isError ? (
                <p className="c-note c-note-critical">{portalText("operations", "your_agents_could_not_be_listed_e13ea4ad")}{campaignRefusalText(voiceAgentList.error)}</p>
              ) : voiceAgentList.data === undefined ? (
                <p className="c-note">{portalText("operations", "listing_your_agents_a2d43f4b")}</p>
              ) : voiceAgentList.data.length === 0 ? (
                <p className="c-note">
                  {portalText("operations", "you_have_no_agents_yet_4198dd3b")}<Link to="/agents/new">{portalText("operations", "build_one_3b84ea96")}</Link> {portalText("operations", "first_a_campaign_is_run_by_an_agent_that_already_exists_7289ef57")}</p>
              ) : campaignVoiceAgent.length === 0 ? (
                <p className="c-note">
                  {portalText("operations", "none_of_your_agents_can_run_a_campaign_yet_a_campaign_need_c6c3eb56")}{" "}
                  <Link to="/agents/new">{portalText("operations", "build_one_3b84ea96")}</Link> {portalText("operations", "first_49ec0838")}</p>
              ) : (
                <>
                  <Select
                    {...control}
                    value={pickedVoiceAgentId}
                    onChange={(event) => {
                      setChosenVoiceAgentId(event.target.value);
                    }}
                  >
                    {pickedVoiceAgentId === "" ? <option value="">{portalText("operations", "choose_an_agent_7d046464")}</option> : null}
                    {campaignVoiceAgent.map((one) => (
                      <option key={one.voiceAgentId} value={one.voiceAgentId}>
                        {one.displayName}
                      </option>
                    ))}
                  </Select>
                  <span className="c-camp-hint">{notListedText(otherVoiceAgent.map((one) => one.displayName))}</span>
                </>
              )
            }
          </Field>
        </div>
      ) : null}

      <div className={classes("c-camp-layout", wide ? "c-camp-layout-wide" : "c-camp-layout-narrow")}>
        <EstimatePanel wide={wide} state={estimateState} start={start} after={after} />

        <div className="c-camp-main">
          <div className="c-camp-column">
            <div className="field">
              {/* The "i" sits beside the label rather than inside it, so the label names only the input. */}
              <span className="c-camp-label c-camp-label-tip">
                <label htmlFor="campName">{portalText("operations", "campaign_name_aa5d0e72")}</label>
                <InfoTip label={portalText("operations", "what_a_campaign_name_can_contain_8080840d")} narrow>
                  {CAMPAIGN_NAME_TIP()}
                </InfoTip>
              </span>
              <Input
                id="campName"
                placeholder={portalText("operations", "autumn_customer_survey_ac050680")}
                maxLength={CAMPAIGN_NAME_MAXIMUM}
                value={name}
                aria-invalid={nameHelp.invalid}
                aria-describedby={nameHelpId}
                {...helpFill.mark("name")}
                onChange={(event) => {
                  changeName(event.target.value);
                }}
              />
              <span id={nameHelpId} className={classes("c-camp-count", nameHelp.invalid && "c-camp-count-invalid")}>
                <span>{nameHelp.message}</span>
                <span className="c-camp-count-figure">
                  {name.length}/{CAMPAIGN_NAME_MAXIMUM}
                </span>
              </span>
            </div>

            <Field label={portalText("operations", "who_to_call_468665d9")}>
              {(control) =>
                contactList.isError ? (
                  <p className="c-note c-note-critical">
                    {portalText("operations", "your_contact_lists_could_not_be_listed_so_none_can_be_pick_f313bd9c")}{campaignRefusalText(contactList.error)}
                  </p>
                ) : contactList.data === undefined ? (
                  <p className="c-note">{portalText("operations", "listing_your_contact_lists_89a3de3e")}</p>
                ) : (
                  <>
                    {contactList.data.row.length === 0 ? (
                      <p className="c-note">{portalText("operations", "you_have_no_contact_lists_yet_8363d940")}</p>
                    ) : (
                      <Select
                        {...control}
                        value={contactListId}
                        disabled={started}
                        onChange={(event) => {
                          setContactListId(event.target.value);
                        }}
                      >
                        {contactListId === "" ? <option value="">{portalText("operations", "pick_a_saved_list_fec8ba15")}</option> : null}
                        {contactList.data.row.map((one, _index, every) => (
                          <option key={one.contactListId} value={one.contactListId}>
                            {every.filter((other) => other.name === one.name).length > 1
                              ? contactListOptionText(one)
                              : `${one.name} · ${countText(one.personCount)} ${one.personCount === 1 ? portalText("operations", "person_d39a4750") : portalText("operations", "people_ab3ccc8d")}`}
                          </option>
                        ))}
                      </Select>
                    )}
                    {started ? <span className="c-camp-hint">{FIXED_ONCE_STARTED()}</span> : null}
                    {editing !== null ? null : (
                    <button type="button" className="c-camp-make-list" onClick={makeList}>
                      <Icon glyph={CAMPAIGN_GLYPH.addressBook} size="2xl" tone="accent" />
                      <span className="c-camp-make-list-body">
                        <span className="c-camp-make-list-title">{portalText("operations", "need_a_different_audience_create_a_contact_list_d2c71171")}</span>
                        <span className="c-camp-make-list-dek">{portalText("operations", "you_ll_come_back_here_with_it_picked_3b73bcc5")}</span>
                      </span>
                      <Icon glyph={CAMPAIGN_GLYPH.arrowRight} size="lg" />
                    </button>
                    )}
                  </>
                )
              }
            </Field>

            {editing !== null ? (
              <div className="field">
                <span className="c-camp-label">{portalText("operations", "calls_from_e7a284a8")}</span>
                <span>{editing.callerIdE164 ?? portalText("operations", "no_caller_id_number_cd0295ee")}</span>
                <span className="c-camp-hint">
                  {FIXED_AT_CREATION()} {VOICEMAIL_SENTENCE(voicemailDetection)}
                </span>
              </div>
            ) : voiceAgentId === "" ? null : (
              <Field label={portalText("operations", "calls_from_e7a284a8")} description={VOICEMAIL_SENTENCE(voicemailDetection)} {...(callerIdRefusal === null ? {} : { error: callerIdRefusal })}>
                {(control) =>
                  trunk.isError ? (
                    <p className="c-note c-note-critical">
                      {portalText("operations", "your_sip_trunks_could_not_be_read_so_no_number_can_be_pick_1764b5d5")}{campaignRefusalText(trunk.error)}
                    </p>
                  ) : trunk.data === undefined ? (
                    <p className="c-note">{portalText("operations", "reading_your_sip_trunk_s_numbers_3ab1d7bb")}</p>
                  ) : callerIdGap !== null ? (
                    <p className="c-note">
                      {callerIdGap} <Link to="/numbers">{portalText("operations", "open_numbers_sip_3118ebd9")}</Link>
                    </p>
                  ) : (
                    <Select
                      {...control}
                      value={callerId}
                      onChange={(event) => {
                        setChosenCallerId(event.target.value);
                        create.reset();
                      }}
                    >
                      <option value="">{portalText("operations", "choose_a_caller_id_number_74a545a6")}</option>
                      {callerIdChoice.map((one) => (
                        <option key={one.phoneNumberId} value={one.phoneNumberId}>
                          {one.e164} · {one.sipTrunkDisplayName}
                        </option>
                      ))}
                    </Select>
                  )
                }
              </Field>
            )}

            <div className="field">
              <span className="c-camp-label">{portalText("operations", "questions_to_ask_6fab385a")}</span>
              {voiceAgentId === "" ? (
                <span className="c-camp-hint">{portalText("operations", "choose_an_agent_to_see_the_questions_it_asks_3ce565e5")}</span>
              ) : questionnaire.isError ? (
                <p className="c-note c-note-critical">
                  {portalText("operations", "the_agent_s_questions_could_not_be_read_55f2c920")}{campaignRefusalText(questionnaire.error)}
                </p>
              ) : questionnaire.data === undefined ? (
                <p className="c-note">{portalText("operations", "reading_the_agent_s_questions_07ee256c")}</p>
              ) : editing !== null && !questionnaire.data.asksQuestionnaire ? (
                <span className="c-camp-hint">
                  {editing.questionSetText}. {FIXED_AT_CREATION()}
                </span>
              ) : questionnaire.data.asksQuestionnaire ? (
                <>
                  <div className="c-camp-set">
                    <div className="c-camp-set-head">
                      <span className="c-camp-set-title">
                        {kickerName === null ? portalText("operations", "the_agent_s_current_set_05da5af2") : portalText("operations", "value_the_agent_s_current_set_c1952e57", { v0: String(kickerName) })}
                      </span>
                      <span className="c-camp-set-facts">
                        <span className="c-camp-set-fact">
                          <Icon glyph={CAMPAIGN_GLYPH.listNumbers} size="sm" />
                          {questionCountText(questionnaire.data.question.length)}
                        </span>
                        <span className="c-camp-set-fact">
                          <Icon glyph={CAMPAIGN_GLYPH.shapes} size="sm" />
                          {questionMixOf(questionnaire.data.question.map((one) => (one.questionKind === "statement" ? "scale" : one.questionKind === "open" || one.questionKind === "number" ? one.questionKind : "choice")))}
                        </span>
                      </span>
                    </div>
                    <VoiceAgentQuestionList question={questionnaire.data.question} look="set" />
                    <p className="c-camp-set-foot">{VOICE_AGENT_QUESTION_NOTE()}</p>
                  </div>
                </>
              ) : (
                <>
                  {/* An agent built with no questionnaire: the campaign's questions are typed here. The design's drop
                      zone is gone — campaigns never upload questions (owner, 2026-10-09: a campaign uses what its
                      agent supports). */}
                  <CampaignQuestionEditor noun="Question" entry={question} onChange={setQuestion} nextKey={nextKey} />
                  <span className="c-camp-hint">
                    {portalText("operations", "scale_answers_are_read_as_one_to_five_open_answers_are_wri_04f1ade0")}</span>
                </>
              )}
            </div>

            <div className="field">
              <span className="c-camp-label">{portalText("operations", "people_already_surveyed_cdbd3e61")}</span>
              {editing === null ? (
                <Segmented
                  name="campaignReturningPerson"
                  legend={portalText("operations", "people_already_surveyed_cdbd3e61")}
                  value={returningPerson}
                  options={[...REPEAT_OPTION]}
                  onChange={setReturningPerson}
                />
              ) : (
                <span>{REPEAT_OPTION.find((one) => one.value === returningPerson)?.label ?? portalText("operations", "ask_them_again_dfe3516a")}</span>
              )}
              <span className="c-camp-hint">
                {repeatNoteOf(returningPerson)}
                {editing === null ? null : <> {FIXED_AT_CREATION()}</>}
              </span>
            </div>

            <CallingHoursEditor
              draft={hoursDraft}
              onChange={setHoursDraft}
              markDays={helpFill.mark("day_of_week")}
              markFrom={helpFill.mark("from_local_time")}
              markTo={helpFill.mark("to_local_time")}
            />

            <div className="field">
              <label className="c-camp-label" htmlFor="campaign-simultaneous-call">
                {portalText("operations", "calls_at_the_same_time_7abbe06f")}{countText(lines)}
              </label>
              <input
                id="campaign-simultaneous-call"
                className="c-camp-range"
                type="range"
                min={1}
                max={ceiling === null ? lines : ceiling}
                value={lines}
                disabled={ceiling === null}
                onChange={(event) => {
                  setSimultaneousCall(Number(event.target.value));
                }}
              />
              <span className="c-camp-range-scale">
                <span>1</span>
                <span>{campaignLineBoundText(bound)}</span>
              </span>
              {campaignLineLimitHint(bound) === null ? null : <span className="c-camp-hint">{campaignLineLimitHint(bound)}</span>}
            </div>

            <div className="field" {...helpFill.mark("attempt")}>
              <span className="c-camp-label">{portalText("operations", "attempts_per_person_5dcdd77e")}</span>
              <Segmented
                name="campaignAttempt"
                legend={portalText("operations", "attempts_per_person_5dcdd77e")}
                value={attempt}
                options={[...ATTEMPT_OPTION]}
                onChange={chooseAttempt}
              />
              {keptAttempts === null ? null : (
                <span className="c-camp-hint">
                  {portalText("operations", "this_campaign_tries_each_person_65e17f9d")}{countText(keptAttempts)} {portalText("operations", "times_pick_one_of_the_choices_to_change_it_e8b72650")}</span>
              )}
              {/* plan-031 VMD-WAPP: after no answer — and a voicemail only while detection is on. */}
              {retryRule === null ? null : <span className="c-camp-hint">{retryRule}</span>}
            </div>

            {attempt === "1" ? null : (
              <div className="field">
                <span id={waitLabelId} className="c-camp-label c-camp-label-tip">
                  {portalText("operations", "wait_before_trying_again_449efb82")}<InfoTip label={portalText("operations", "how_retries_are_timed_ba304ee2")}>{WAIT_TIP()}</InfoTip>
                </span>
                <div role="radiogroup" aria-labelledby={waitLabelId} className="c-camp-waits" {...helpFill.mark("wait_minute")}>
                  {WAIT_CHOICE.map((one) => (
                    <button
                      key={one.minute}
                      type="button"
                      role="radio"
                      aria-checked={waitMinute === one.minute}
                      className={classes("c-camp-wait", waitMinute === one.minute && "c-camp-wait-on")}
                      onClick={() => {
                        setWaitMinute(one.minute);
                      }}
                    >
                      <span className="c-camp-wait-label">{one.label}</span>
                      <span className="c-camp-wait-dek">{one.dek}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="c-camp-when-card">
              <h3 className="c-camp-when-title">{portalText("operations", "when_it_runs_e62158d3")}</h3>
              <div className="c-camp-when-grid">
                <span className="field">
                  <label htmlFor="campDate">{portalText("operations", "starts_fc612a2f")}</label>
                  <span className="c-camp-date">
                    <Input
                      id="campDate"
                      type="text"
                      inputMode="numeric"
                      placeholder="DD/MM/YYYY"
                      maxLength={10}
                      value={startDateText}
                      disabled={started}
                      {...helpFill.mark("start_date")}
                      aria-label={portalText("operations", "start_date_ff99f5b5")}
                      onChange={(event) => {
                        setStartDateText(event.target.value);
                      }}
                    />
                    <Icon glyph={CAMPAIGN_GLYPH.calendarBlank} size="md" />
                  </span>
                </span>
                <span className="field">
                  <label htmlFor="campTime">{portalText("operations", "first_call_19a0987c")}</label>
                  <Input
                    id="campTime"
                    type="text"
                    inputMode="numeric"
                    placeholder="HH:MM"
                    maxLength={5}
                    value={startTime}
                    disabled={started}
                    {...helpFill.mark("start_time")}
                    onChange={(event) => {
                      setStartTime(event.target.value);
                    }}
                  />
                </span>
                <span className="field">
                  <label htmlFor="campEnd">{portalText("operations", "ends_91856dcb")}</label>
                  <span className="c-camp-date">
                    <Input
                      id="campEnd"
                      type="text"
                      inputMode="numeric"
                      placeholder="DD/MM/YYYY"
                      maxLength={10}
                      value={endDateText}
                      {...helpFill.mark("end_date")}
                      aria-label={portalText("operations", "end_date_89d10cd6")}
                      onChange={(event) => {
                        setEndDateText(event.target.value);
                      }}
                    />
                    <Icon glyph={CAMPAIGN_GLYPH.calendarBlank} size="md" />
                  </span>
                </span>
              </div>
              <div className="c-camp-when-notes">
                <span className="c-camp-when-note">
                  <Icon glyph={CAMPAIGN_GLYPH.playCircle} size="lg" tone="accent" />
                  {started ? (
                    <span>{FIXED_ONCE_STARTED()}</span>
                  ) : (
                    <span className={classes(startNote.warning && "c-camp-warn")}>{startNote.text}</span>
                  )}
                </span>
                {endNote.text === "" ? null : (
                  <span className="c-camp-when-note">
                    <Icon glyph={CAMPAIGN_GLYPH.finishLine} size="lg" tone="accent" />
                    <span className={classes(endNote.warning && "c-camp-warn")}>{endNote.text}</span>
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------------------------------------
// The estimate panel
// -------------------------------------------------------------------------------------------

interface EstimateState {
  readonly ready: boolean;
  readonly voiceAgentChosen: boolean;
  readonly windowProblem: string | null;
  readonly dayCount: number;
  readonly pending: boolean;
  readonly refreshing: boolean;
  readonly failure: string | null;
  readonly estimate: CampaignEstimateView | undefined;
  readonly lines: number;
  readonly attemptsPerPerson: number;
  readonly plan: CampaignPlan | null;
  /** plan-015 CMP: the end date as typed (empty = no end), for the estimate's last row. */
  readonly endDateText: string;
}

/** The one reason there is no estimate yet, or `null` when there is one. */
function noEstimateText(state: EstimateState): string | null {
  if (!state.voiceAgentChosen) return portalText("operations", "choose_an_agent_to_see_an_estimate_058fd50e");
  if (state.windowProblem !== null) return portalText("operations", "fix_the_calling_hours_to_see_an_estimate_756799f5");
  if (state.dayCount === 0) return portalText("operations", "pick_at_least_one_day_to_see_an_estimate_0838a53d");
  if (!state.ready) return portalText("operations", "add_a_call_list_to_see_how_long_it_takes_and_what_it_costs_13e8fbbd");
  if (state.failure !== null) return portalText("operations", "no_estimate_value_f0d57547", { v0: String(state.failure) });
  if (state.pending || state.estimate === undefined) return portalText("operations", "working_out_the_estimate_e84fdb3d");
  return null;
}

function bandText(estimate: CampaignEstimateView): string {
  return `${formatMoney(estimate.low)}–${formatMoney(estimate.high)}`;
}

/**
 * The design's ESTIMATE: a side panel beside the form on a wide window (rows, the billing sentence and
 * Start), a sticky one-line bar above it on a narrow one. The cost is a band with its assumption.
 */
function EstimatePanel({
  wide,
  state,
  start,
  after,
}: {
  readonly wide: boolean;
  readonly state: EstimateState;
  readonly start: ReactNode;
  readonly after: ReactNode;
}): ReactNode {
  usePortalTranslation("operations");
  const reason = noEstimateText(state);
  const estimate = reason === null ? state.estimate : undefined;
  const noZone =
    estimate === undefined || estimate.noTimeZoneCount === 0 ? null : noTimeZoneText(estimate.noTimeZoneCount);

  if (!wide) {
    return (
      <>
        <aside aria-label={portalText("operations", "campaign_estimate_d4ae2d86")} className="c-camp-estimate-bar" aria-busy={state.refreshing}>
          <span className={classes("c-camp-estimate-line", state.failure !== null && "c-note-critical")}>
            {estimate === undefined
              ? reason === "Add a call list to see how long it takes and what it costs."
                ? portalText("operations", "add_a_call_list_to_see_the_estimate_e1b0bf3a")
                : reason
              : portalText("operations", "value_people_about_value_value_value_4124dc78", { v0: String(countText(estimate.personCount)), v1: String(countText(estimate.dayCount)), v2: String(estimate.dayCount === 1 ? portalText("operations", "day_a2620cbc") : portalText("operations", "days")), v3: String(bandText(estimate)) })}
          </span>
          <span className="c-camp-estimate-actions">{start}</span>
        </aside>
        {/* Under the sticky bar, in the page's flow: what it leaves out and what Start still needs. */}
        <div className="c-camp-estimate-after">
          {estimate === undefined ? null : <span className="c-visually-hidden">{estimate.assumption}</span>}
          {estimate === undefined ? null : (
            <span className="c-camp-hint">{outboundRateTextOf(estimate.talkRateKind, estimate.talkRatePerMinute)}</span>
          )}
          {noZone === null ? null : <span className="c-camp-hint">{noZone}</span>}
          {after}
        </div>
      </>
    );
  }

  const row: { readonly label: string; readonly value: string; readonly title?: string }[] = [];
  if (estimate !== undefined) {
    row.push({ label: portalText("operations", "people_to_call_5d943aba"), value: countText(estimate.personCount) });
    row.push({ label: portalText("operations", "lines_at_once_fd0496f5"), value: countText(state.lines) });
    if (state.plan !== null) {
      row.push({ label: portalText("operations", "first_calls_done_by_a4cfe67d"), value: dayTextOf(state.plan.lastFirst) });
      row.push({
        label: state.attemptsPerPerson > 1 ? portalText("operations", "last_retry_by_662be4b2") : portalText("operations", "campaign_ends_93c386cb"),
        value: dayTextOf(lastDialDayOf(state.plan, state.endDateText)),
      });
    }
    row.push({ label: portalText("operations", "talk_time_cost_c09e4980"), value: bandText(estimate), title: estimate.assumption });
  }
  // plan-017 (Q5, OQ-1): the band is at the outbound rate of the route it would dial on, and says which.
  const rateText = estimate === undefined ? null : outboundRateTextOf(estimate.talkRateKind, estimate.talkRatePerMinute);

  return (
    <aside aria-label={portalText("operations", "campaign_estimate_d4ae2d86")} className="c-camp-estimate-panel" aria-busy={state.refreshing}>
      <span className="c-camp-estimate-eyebrow">{portalText("operations", "estimate_2352230a")}</span>
      {estimate === undefined ? (
        <span className={classes("c-camp-estimate-reason", state.failure !== null && "c-note-critical")}>{reason}</span>
      ) : (
        <>
          <dl className="c-camp-estimate-rows">
            {row.map((one) => (
              <div className="c-camp-estimate-row" key={one.label}>
                <dt>{one.label}</dt>
                <dd title={one.title}>{one.value}</dd>
              </div>
            ))}
          </dl>
          {rateText === null ? null : <span className="c-camp-estimate-note">{rateText}</span>}
          <span className="c-camp-estimate-note">
            {portalText("operations", "billed_only_for_minutes_the_agent_is_talking_updates_as_yo_a319500e")}</span>
          {noZone === null ? null : <span className="c-camp-estimate-note">{noZone}</span>}
          <span className="c-visually-hidden">{estimate.assumption}</span>
        </>
      )}
      <div className="c-camp-estimate-foot">
        {start}
        {after}
      </div>
    </aside>
  );
}

// -------------------------------------------------------------------------------------------
// Words
// -------------------------------------------------------------------------------------------

function questionCountText(count: number): string {
  return `${countText(count)} ${count === 1 ? portalText("operations", "question_3b2be2e2") : portalText("operations", "questions_ff3d39b6")}`;
}

/**
 * The design's line under the agent picker: "Only agents that make calls can run a campaign. Roma orders
 * only answers calls, so it is not listed." — with the member's own agents named.
 */
export function notListedText(otherName: readonly string[]): string {
  const lead = portalText("operations", "only_agents_that_make_calls_can_run_a_campaign_19df6011");
  if (otherName.length === 0) return lead;
  if (otherName.length === 1) return portalText("operations", "value_value_only_answers_calls_so_it_is_not_listed_db90c6f4", { v0: String(lead), v1: String(otherName[0] ?? "") });
  const named =
    otherName.length === 2
      ? portalText("operations", "value_and_value_dcf74a8c", { v0: String(otherName[0] ?? ""), v1: String(otherName[1] ?? "") })
      : portalText("operations", "value_and_value_dcf74a8c", { v0: String(otherName.slice(0, -1).join(", ")), v1: String(otherName[otherName.length - 1] ?? "") });
  return portalText("operations", "value_value_only_answer_calls_or_have_no_published_version_ecaeb25d", { v0: String(lead), v1: String(named) });
}

/**
 * CL #123: one list in the "Who to call" picker — its name, its people and when it came in, so two
 * lists with the same name ("Basic comma CSV · 3 people") can be told apart. A name used once reads as
 * the design draws it, without the arrival.
 */
export function contactListOptionText(one: {
  readonly name: string;
  readonly personCount: number;
  readonly arrivalText: string;
}): string {
  return `${one.name} · ${countText(one.personCount)} ${one.personCount === 1 ? portalText("operations", "person_d39a4750") : portalText("operations", "people_ab3ccc8d")} · ${one.arrivalText}`;
}

/**
 * CL #118: the people the estimate leaves out because their row gives no time zone — calling hours
 * are each person's own, so the dialler skips them rather than guess.
 */
export function noTimeZoneText(count: number): string {
  return portalText("operations", "campaign.noTimeZone", { count: count, v0: countText(count) });
}

/**
 * Whether an agent can run a campaign (CL #134): built from the survey template (the one outbound
 * template today — its code is `survey`, or ends in `_survey` for a business-specific variant) and
 * with a published version, which the API requires (`agent_version_not_ready`).
 */
/** The words under the name field: a problem, "Looks good.", or nothing while it is empty. */
interface CampaignNameCheck {
  readonly message: string;
  readonly invalid: boolean;
}

/**
 * The design's New campaign name check, as it is typed (plan-015 NAM): the API's rule (`campaignNameProblem`) and
 * uniqueness by `nameKeyOf` against the workspace's campaign names. Nothing is said while the field is empty.
 */
export function campaignNameCheckOf(raw: string, taken: readonly string[]): CampaignNameCheck {
  if (raw.trim() === "") return { message: "", invalid: false };
  const problem = campaignNameProblem(raw) ?? (isNameTaken(raw, taken) ? CAMPAIGN_NAME_TAKEN_MESSAGE : null);
  return problem === null ? { get message() { return portalText("operations", "looks_good_6e842722"); }, invalid: false } : { get message() { return translatedClientError(problem) ?? problem; }, invalid: true };
}

export function runsCampaigns(one: { readonly agentTemplateCode: string; readonly liveAgentVersionId: string | null }): boolean {
  const outbound = one.agentTemplateCode === "survey" || one.agentTemplateCode.endsWith("_survey");
  return outbound && one.liveAgentVersionId !== null;
}
